import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";

// The update banner is only relevant to signed-in app users stuck on a stale
// PWA build — never on the public marketing site / share pages, which are
// "just a website" for prospects.
const APP_PREFIXES = ["/dashboard", "/jobs", "/activity", "/account", "/testers", "/onboarding"];
const isAppRoute = (path) => APP_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));

// Signature = the set of /static/js chunk paths the page references. It changes
// on every production build (hashed filenames), so comparing the live page's
// signature to the one we booted with tells us a new version was deployed.
const sigFromHtml = (html) => {
  const out = [];
  const re = /src="([^"]+\.js)"/g;
  let m;
  while ((m = re.exec(html))) {
    try { out.push(new URL(m[1], window.location.origin).pathname); } catch { /* ignore */ }
  }
  return out.filter((p) => p.includes("/static/js/")).sort().join("|");
};

const currentSig = () =>
  [...document.querySelectorAll("script[src]")]
    .map((s) => { try { return new URL(s.src).pathname; } catch { return ""; } })
    .filter((p) => p.includes("/static/js/"))
    .sort()
    .join("|");

export function UpdatePrompt() {
  const [show, setShow] = useState(false);
  const baseRef = useRef(currentSig());
  const { pathname } = useLocation();

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      if (stopped || document.hidden || !baseRef.current) return;
      try {
        const res = await fetch(`${window.location.origin}/?_u=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const sig = sigFromHtml(await res.text());
        if (sig && sig !== baseRef.current) setShow(true);
      } catch { /* offline — try again later */ }
    };
    const interval = setInterval(check, 60000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    const first = setTimeout(check, 8000);
    return () => {
      stopped = true;
      clearInterval(interval);
      clearTimeout(first);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  if (!show || !isAppRoute(pathname)) return null;

  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-emerald-500/30 bg-[#0B111E]/95 backdrop-blur-lg animate-in slide-in-from-bottom"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
        <RefreshCw className="h-4 w-4 shrink-0 text-emerald-400" />
        <span className="min-w-0 flex-1 text-sm font-semibold text-white">A new version is available</span>
        <button
          data-testid="update-dismiss"
          onClick={() => setShow(false)}
          className="px-1 text-sm text-neutral-400 transition-colors hover:text-neutral-200"
        >
          Later
        </button>
        <button
          data-testid="update-now-btn"
          onClick={() => window.location.reload()}
          className="shrink-0 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-black transition-transform active:scale-95"
        >
          Update
        </button>
      </div>
    </div>
  );
}
